import React, { useEffect, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, IconButton, Grid, Paper, Typography, useTheme, Chip, Skeleton, Divider } from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getReservation, setBookingStepToPaymentConfirmation } from '~/actions';
import StandardPage from '~/layouts/StandardPage';
import { Box } from '@mui/system';
import { BOOKING_CONTEXT, PAYMENT_STATUS, STATUS } from '~/literals';
import { FlightReservation, Leg, PassengerLegBaggage, PassengerType, RootState } from '~/types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Done, EmailRounded } from '@mui/icons-material';
import { getFriendlyPassengerType, getPassengerLegBaggage } from '~/modules/helpers';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TripDestinationAndArrival from '~/components/TripDestinationAndArrival';
import FlightCardItemDetailsCollapsible from '~/components/FlightCardItemDetailsCollapsible';
import FlightBookingPriceSummary from '~/components/FlightBookingPriceSummary';
// import EmailReservationModal from '~/components/EmailReservationModal';
import useMediaQuery from '@mui/material/useMediaQuery';

function FlightReservationPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paymentStatus = queryParams.get('status');
  const isTab = useMediaQuery(theme.breakpoints.down('md'));


  const status = useSelector<RootState, string>(({ flightBooking }) => flightBooking.pageStatus);
  // const sendEmailStatus = useSelector<RootState, string>(({ flightBooking }) => flightBooking.emailSendStatus);
  const reservationDetails = useSelector<RootState, FlightReservation | null>(({ flightBooking }) => flightBooking.reservation);
  const errorMessage = useSelector<RootState, string | undefined>(({ flightBooking }) => flightBooking?.errorMessage);

  // const [isEmailReservationModalOpen, setIsEmailReservationModalOpen] = useState(false);
  // const [isEmailReservationModalValidation, setIsEmailReservationModalValidationMode] = useState(false);
  // const [emailReservationModalValidationMessage, setEmailReservationModalValidationMessage] = useState('');
  // const [reservationEmailAddress, setReservationEmailAddress] = useState('');

  // const handleOpenEmailReservationModal = () => {
  //   setIsEmailReservationModalOpen(true);
  //   setEmailReservationModalValidationMessage('');
  //   setIsEmailReservationModalValidationMode(false);
  //   dispatch(sendReservationEmailReset());
  // }
  // const handleCloseEmailReservationModal = () => setIsEmailReservationModalOpen(false);

  // const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setReservationEmailAddress(event.target.value);
  //   if (isValidEmail(reservationEmailAddress)) {
  //     setIsEmailReservationModalValidationMode(false);
  //     setEmailReservationModalValidationMessage('');
  //   }
  // };

  // const handleSendEmail = () => {
  //   if (!reservationEmailAddress || !isValidEmail(reservationEmailAddress)) {
  //     setIsEmailReservationModalValidationMode(true);
  //     setEmailReservationModalValidationMessage("Please enter a valid email address.");
  //   } else if (id && reservationEmailAddress) {
  //     dispatch(sendReservationEmail({ bookingId: id, email: reservationEmailAddress }))
  //   }
  // };

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS)
      dispatch(
        getReservation(id || ''),
      );
    dispatch(setBookingStepToPaymentConfirmation())
    
    // Track successful booking/reservation completion
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      ReactGA.event({
        category: 'booking',
        action: 'booking_completed',
        label: `reservation_${id}`,
      });
    }
  }, [dispatch, id]);

  // useEffect(() => {
  //   if (sendEmailStatus === STATUS.ERROR) {
  //     setEmailReservationModalValidationMessage("Could not send email, please try again.");
  //     setIsEmailReservationModalValidationMode(true);
  //   }
  // }, [dispatch, sendEmailStatus]);

  let reservationInfo;
  let bannerContent;
  let displayError;
  let emailReservationModal;
  let passengerLegBaggage: PassengerLegBaggage[] = [];

  if (reservationDetails?.bookingDetails?.offers[0].passengerPricing) {
    passengerLegBaggage = getPassengerLegBaggage(reservationDetails?.bookingDetails?.offers[0].passengerPricing, reservationDetails?.bookingDetails?.passengers || []);
  }

  if (reservationDetails && status === STATUS.SUCCESS && paymentStatus === PAYMENT_STATUS.SUCCESS) {
    const tripOutboundLegs: Leg[] = reservationDetails.bookingDetails.offers.length ?
      reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs : [];


    const trip = <TripDestinationAndArrival
      departure={tripOutboundLegs[0].departure}
      arrival={tripOutboundLegs[tripOutboundLegs.length - 1].arrival}
      flightType={reservationDetails.bookingDetails.offers[0].flightType}
    />

    bannerContent = <Typography variant="h4" color="#fff" sx={{ fontSize: { xs: 18, sm: 28, md: 34 } }}>Your trip to {tripOutboundLegs[tripOutboundLegs.length - 1].arrival.city.friendlyName}.</Typography>;

    // emailReservationModal = <EmailReservationModal
    //   open={isEmailReservationModalOpen}
    //   onClose={handleCloseEmailReservationModal}
    //   onSendEmail={handleSendEmail}
    //   email={reservationEmailAddress}
    //   onEmailChange={handleEmailChange}
    //   emailSendStatus={sendEmailStatus}
    //   validationMessage={isEmailReservationModalValidation ? emailReservationModalValidationMessage : ''}
    // />

    reservationInfo = (
      <Box>
        <Grid container>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
              <Grid container spacing={5}>
                <Grid item xs={2} lg={1}>
                  <CheckCircleIcon
                    style={{
                      color: theme.palette.success.main,
                      fontSize: isTab ? 40 : 70
                    }}
                  />
                </Grid>
                <Grid item xs={10} lg={7}>
                  <Typography variant='h5' style={{ color: theme.palette.success.main }}>
                    {
                      reservationDetails.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY ?
                        'Your itinerary reservation is complete.'
                        :
                        `Your flight booking is complete! Your airline reference is ${reservationDetails.PNRRecords.map(item => item.PNR).join(', ')}`
                    }
                  </Typography>
                  <Typography variant='body1'>
                    Thank you for choosing Tripenger! We will sent an email to
                    <strong> {reservationDetails?.bookingDetails?.tripContactPersons[0]?.contactDetails?.email}</strong> with your {reservationDetails.bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY && <span>ticket and</span>} itinerary.
                  </Typography>
                  <Typography variant="subtitle1">
                    Your Tripenger reference code is: <strong>{reservationDetails.localPNRCode}</strong>.
                    {reservationDetails.bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY && <span> Please use the airline reference code for seat selection and online check-in, if available on airline website.</span>}
                  </Typography>
                </Grid>
                {/* 
                TODO: Will be implemented later
                <Grid item xs={12} lg={4} >
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Mail />}
                        onClick={handleOpenEmailReservationModal}
                      >
                        Email
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<DownloadIcon />}
                      >
                        Download
                      </Button>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ManageAccountsIcon />}
                      >
                        Manage
                      </Button>
                    </Grid>
                  </Grid>
                </Grid> 
                */}
              </Grid>
              <Grid item xs={12} marginTop={5} >
                <Divider />
              </Grid>
              <Grid item xs={12} marginTop={5} >
                {trip}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant='h5' marginBottom={3} marginTop={5} >Trip Details</Typography>
            <>
              <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  boxSizing="border-box"
                  justifyContent="space-between"
                  pl={2}
                  pr={2}
                >
                  <Chip label="Confirmed" color="success" size="small" icon={<Done />} />
                  {
                    reservationDetails.PNRRecords.length > 0 &&
                    <Typography variant='h6'>
                      <Tooltip title="You can use this reference for selecting your seat and checking in online on the airline's website.">
                        <IconButton size="small" sx={{ verticalAlign: 'middle', mr: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <small>{reservationDetails.PNRRecords.length > 1 ? 'References' : 'Reference'}:</small>{reservationDetails.PNRRecords.map(item => item.PNR).join(', ')}
                    </Typography>
                  }
                </Box>
                <FlightCardItemDetailsCollapsible
                  offer={reservationDetails.bookingDetails.offers[0]}
                  passengerPricing={reservationDetails.bookingDetails.offers[0].passengerPricing}
                  passengerLegBaggage={passengerLegBaggage}
                />
              </Paper>
            </>
          </Grid>

          <Grid item xs={12} marginTop={5}>
            <Grid container spacing={5}>
              <Grid item xs={12} lg={6} marginTop={5}>
                <Typography variant='h5' marginBottom={3}>Passenger Details</Typography>
                <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
                  {
                    reservationDetails.bookingDetails.passengers.map(pax => (
                      <Typography variant='subtitle1' key={pax.id}>
                        {pax.firstName} {pax.lastName} ({getFriendlyPassengerType(pax.passengerType as PassengerType)})
                      </Typography>
                    ))
                  }
                </Paper>
              </Grid>
              <Grid item xs={12} lg={6} marginTop={5}>
                <Typography variant='h5' marginBottom={3}>Contact Details</Typography>
                <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
                  {
                    reservationDetails.bookingDetails.tripContactPersons.map(person => (
                      <React.Fragment key={person.id}>
                        <Typography variant='body1'>
                          {person.firstName} {person.lastName}
                        </Typography>
                        <Box display='flex' alignItems='center'>
                          <EmailRounded />
                          <Typography variant='body1' ml={1}>
                            {person.contactDetails?.email}
                          </Typography>
                        </Box>
                      </React.Fragment>
                    ))
                  }

                </Paper>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} marginTop={5}>
            <Typography variant='h5' marginBottom={3}>Price Details</Typography>
            <FlightBookingPriceSummary
              passengers={reservationDetails.bookingDetails.passengers || []}
              passengerPricing={reservationDetails.bookingDetails.offers[0].passengerPricing}
              currency={reservationDetails.bookingDetails.offers[0].price.currency}
              agentFeesOnly={reservationDetails.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY}
              agentFees={reservationDetails.bookingDetails.agentFees}
              bookingContext={reservationDetails.bookingContext}
            />
          </Grid>

        </Grid>
      </Box>
    );
  }
  else if (errorMessage && (status === STATUS.ERROR || status === STATUS.SUCCESS)) {
    bannerContent = <Typography variant="h5" color="#fff">We encountered an issue and could not finalize your reservation.</Typography>;
    displayError = (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ p: 2 }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong.
        </Typography>
        <Typography variant="body1" gutterBottom>
          {errorMessage}
        </Typography>
      </Box>
    )
  }
  else if (paymentStatus !== PAYMENT_STATUS.SUCCESS) {
    bannerContent = <Typography variant="h5" color="#fff">
      {paymentStatus === PAYMENT_STATUS.FAILED ? 'Payment failed.' : ''}
      {paymentStatus === PAYMENT_STATUS.CANCELLED ? 'Payment was cancelled.' : ''}
    </Typography>;
    displayError = (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ p: 2 }}
      >
        <ErrorOutlineIcon color="warning" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h6" color="warning" gutterBottom>
          {paymentStatus === PAYMENT_STATUS.FAILED ? 'Payment failed.' : ''}
          {paymentStatus === PAYMENT_STATUS.CANCELLED ? 'Payment was cancelled.' : ''}
        </Typography>
        <Typography variant="body1" gutterBottom>
          Your reservation can not be completed at this time. Please try again later.
        </Typography>
      </Box>
    )
  }

  if (status === STATUS.RUNNING && paymentStatus === PAYMENT_STATUS.SUCCESS) {
    reservationInfo = (
      <Grid container spacing={5}>
        {[150, 150, 150].map((h, i) => <Grid item xs={12} key={i}><Skeleton variant="rectangular" width='100%' height={h} /></Grid>)}
      </Grid>
    );

    bannerContent = <Typography variant="h6"><Skeleton width="100%" height={20} sx={{ bgcolor: '#fff' }} /></Typography>
  }

  return (
    <StandardPage
      bannerContent={bannerContent}
    >
      {reservationDetails && status === STATUS.SUCCESS && paymentStatus === PAYMENT_STATUS.SUCCESS ? (
        <Helmet>
          <title>{`Flight Reservation Confirmed - ${reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs[0].departure.city.friendlyName} to ${reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs[reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs.length - 1].arrival.city.friendlyName}`}</title>
          <meta name="description" content={`Your flight reservation from ${reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs[0].departure.city.friendlyName} to ${reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs[reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs.length - 1].arrival.city.friendlyName} is confirmed. Reference: ${reservationDetails.localPNRCode}`} />
        </Helmet>
      ) : status === STATUS.ERROR || (status === STATUS.SUCCESS && errorMessage) ? (
        <Helmet>
          <title>Reservation Error - Unable to Complete Flight Booking</title>
          <meta name="description" content="We encountered an issue while processing your flight reservation. Please try again or contact customer support." />
        </Helmet>
      ) : paymentStatus === PAYMENT_STATUS.FAILED ? (
        <Helmet>
          <title>Payment Failed - Flight Reservation Incomplete</title>
          <meta name="description" content="Your payment was not successful. Please try again to complete your flight reservation." />
        </Helmet>
      ) : paymentStatus === PAYMENT_STATUS.CANCELLED ? (
        <Helmet>
          <title>Payment Cancelled - Flight Reservation Incomplete</title>
          <meta name="description" content="Your payment was cancelled. You can try again to complete your flight reservation." />
        </Helmet>
      ) : (
        <Helmet>
          <title>Processing Flight Reservation...</title>
          <meta name="description" content="Please wait while we process your flight reservation details." />
        </Helmet>
      )}
      {displayError}
      {reservationInfo}
      {emailReservationModal}
    </StandardPage>
  );
}

export default FlightReservationPage;
